import { ReactElement } from "react"
import { updateScenario } from "../logic/scenarios/validation"
import * as func from "../logic/scenarios/functions"
import { API, APIs, OS, OSs, Scenario } from "../logic/scenarios/data"
import Choice, { ChoiceState, OptionProps } from "./WizardChoice"
import { mapRecord } from "../util/functions"

type WizardOptionsProps = {
    scenario: Scenario
    setScenario: (scenario: Scenario) => void
    step: number
    setStep: (step: number) => void
}
const showAllQuestions = true

const getBoolProps : (yesText?: string, noText?: string, yesImage?: string, noImage?: string) => OptionProps<boolean>[] = (yesText, noText, yesImage, noImage) => [
    { key: true, name: yesText||'Yes', icon: yesImage },
    { key: false, name: noText||'No', icon: noImage },
]

const osOptions : OptionProps<string>[] = mapRecord(OSs, (key: string, value: OS) => ({ key, name: value.name, icon: value.icon }))
const apiOptions : OptionProps<string>[] = mapRecord(APIs, (key: string, value: API) => ({ key, name: value.name, icon: value.icon }))

const frameworkOptions = (scenario: Scenario): OptionProps<string>[] => [
    { key:'', name:'None' },
    ...Object.entries(APIs[scenario.api].frameworks ?? {}).map(([fwKey, fw]) => ({ key: fwKey, name: fw.name, icon: fw.icon }))
]

const packageManagerOptions = (scenario: Scenario): OptionProps<string>[] => [
    { key:'', name:'None' },
    ...Object.entries(APIs[scenario.api].packageManagers ?? {}).map(([pmKey, pm]) => ({ key: pmKey, name: pm.name, icon: pm.icon }))
]

const getState = (index: number, current: number) : ChoiceState => {
    if(index == current) {
        return 'OPEN';
    }
    return (index < current || current == -1 || showAllQuestions) ? 'CLOSED' : 'INVISIBLE'
}

export const WizardOptions = ({ scenario, setScenario, step: currentStep, setStep }: WizardOptionsProps): ReactElement[] => {
    const go = (step: number, scenario: Scenario) => {
        setStep(step)
        setScenario(scenario)
    }

    const createProgress = <T,>(scenario: Scenario, key: keyof Scenario, step: number) => (value: T) => {
        const updatedScenario = updateScenario(scenario, key, value)
        go(step + 1, updatedScenario)
    }

    const makeChoice = <T,>(step: number, key: keyof Scenario, value: T, title: string, question: string, description: string | undefined, options: OptionProps<T>[]) =>
    Choice<T>({
        key: `step-${step}`,
        jump: () => go(step, scenario),
        progress: createProgress(scenario, key, step),
        title, question, value, options, description,
        state: getState(step, currentStep)
    })

    function* generateChoices(): Generator<ReactElement> {
        let i = 0
        yield makeChoice(i++, 'os', scenario.os, "Operating system", "On which operating system do you want to run Gurobi?", "Depending on your operating system, you may need to perform additional steps like adding Gurobi to your path.", osOptions)
        yield makeChoice(i++, 'api', scenario.api, "API (Programming language)", "Which Gurobi API (programming language) would you like to use?", "Gurobi can be accessed from a large set of programming languages. The instructions will show you code examples using the programming language of your choice.", apiOptions)
        if (func.hasFrameworks(scenario)) {
            yield makeChoice(i++, 'framework', scenario.framework, "Modeling framework", "Do you want to use a third-party modeling framework?", "Modeling frameworks help you define a mathematical model independent of the choice of a solver.", frameworkOptions(scenario))
        }
        if (func.hasPackageManagers(scenario)) {
            yield makeChoice(i++, 'packageManager', scenario.packageManager, "Package manager", "Do you want to use a package manager?", "Package managers may help you install Gurobi, as well as third party frameworks.", packageManagerOptions(scenario))
        }
        if (func.isComputeServer(scenario)) {
            yield makeChoice(i++, 'clusterManager', scenario.clusterManager, "Cluster manager", "Do you want to run Gurobi Cluster Manager?", "Cluster Manager is a key component of Gurobi Compute Server. It gives you additional capabilities like user management, a graphical dashboard, and asynchronous solving. Most users should install Cluster Manager. There are some unusual cases where Cluster Manager should not be installed. If yours is one of those, please indicate below.", getBoolProps('Install', 'Do not install'))
        }
        if (func.canContainerizeClient(scenario)) {
            yield makeChoice(i++, 'containerClient', scenario.containerClient, 'Application in container', 'Do you want to run your application in a Docker container?', "Running your application in a container may require a different way of setting up a license and/or installing Gurobi.", getBoolProps('In container', 'On a machine', 'docker.svg', 'vm.svg'))
        }
        if (func.canContainerizeServer(scenario)) {
            const components = scenario.clusterManager ? 'Compute Server and Cluster Manager' : 'Cluster Manager'
            yield makeChoice(i++, 'containerServer', scenario.containerServer, `Compute Server in container`, `Do you want to run Gurobi ${components} in a Docker container?`, `Gurobi ${components} can run in containers; we provide Docker base images as a starting point.`, getBoolProps('In container', 'On machine', 'docker.svg', 'vm.svg'))
        }
    }

    return Array.from(generateChoices())
}
